import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getProjectTree, createAspect, deleteAspect, type AspectTreeNode } from '../lib/api';
import AspectTree from '../components/AspectTree';
import MemoryEditor from '../components/MemoryEditor';
import Breadcrumbs from '../components/Breadcrumbs';
import styles from './ProjectDetail.module.css';

export default function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>();
  const [selectedAspect, setSelectedAspect] = useState<AspectTreeNode | null>(null);
  const [showCreateAspect, setShowCreateAspect] = useState(false);
  const [newAspectName, setNewAspectName] = useState('');
  const [newAspectParent, setNewAspectParent] = useState<string | undefined>();
  
  const queryClient = useQueryClient();
  
  const { data: tree, isLoading, error } = useQuery({
    queryKey: ['project-tree', projectId],
    queryFn: () => getProjectTree(projectId!),
    enabled: !!projectId,
  });
  
  const createAspectMutation = useMutation({
    mutationFn: () => createAspect(projectId!, newAspectName, undefined, newAspectParent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-tree', projectId] });
      setShowCreateAspect(false);
      setNewAspectName('');
      setNewAspectParent(undefined);
    },
  });
  
  const deleteAspectMutation = useMutation({
    mutationFn: (aspectId: string) => deleteAspect(aspectId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project-tree', projectId] });
      setSelectedAspect(null);
    },
  });
  
  // Build breadcrumb path for selected aspect
  const getBreadcrumbs = (aspect: AspectTreeNode | null): Array<{ id: string; name: string }> => {
    if (!aspect) return [];
    
    const parts = aspect.path.split('/');
    const crumbs: Array<{ id: string; name: string }> = [];
    
    const findAspect = (nodes: AspectTreeNode[], id: string): AspectTreeNode | null => {
      for (const node of nodes) {
        if (node.aspectId === id) return node;
        const found = findAspect(node.children, id);
        if (found) return found;
      }
      return null;
    };
    
    for (const partId of parts) {
      const found = findAspect(tree?.aspects || [], partId);
      if (found) {
        crumbs.push({ id: found.aspectId, name: found.name });
      }
    }
    
    return crumbs;
  };
  
  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner}></div>
        <span>Loading project...</span>
      </div>
    );
  }
  
  if (error || !tree) {
    return (
      <div className={styles.error}>
        <h2>Error loading project</h2>
        <p>{error instanceof Error ? error.message : 'Project not found'}</p>
        <Link to="/" className="btn btn-secondary">Back to Dashboard</Link>
      </div>
    );
  }
  
  return (
    <div className={styles.projectDetail}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <Link to="/" className={styles.backLink}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
          </Link>
          <div>
            <h1>{tree.project.name}</h1>
            {tree.project.groupName && (
              <span className={styles.group}>{tree.project.groupName}</span>
            )}
          </div>
        </div>
        <button 
          className="btn btn-primary"
          onClick={() => {
            setNewAspectParent(selectedAspect?.aspectId);
            setShowCreateAspect(true);
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          New Aspect
        </button>
      </header>
      
      {showCreateAspect && (
        <div className={styles.createForm}>
          <h3>Create New Aspect</h3>
          <p className={styles.createHint}>
            {newAspectParent 
              ? `Creating under: ${selectedAspect?.name}` 
              : 'Creating at root level'}
          </p>
          <div className={styles.formFields}>
            <input
              type="text"
              className="input"
              placeholder="Aspect name"
              value={newAspectName}
              onChange={(e) => setNewAspectName(e.target.value)}
              autoFocus
            />
          </div>
          <div className={styles.formActions}>
            <button 
              className="btn btn-secondary"
              onClick={() => {
                setShowCreateAspect(false);
                setNewAspectName('');
              }}
            >
              Cancel
            </button>
            <button 
              className="btn btn-primary"
              onClick={() => createAspectMutation.mutate()}
              disabled={!newAspectName || createAspectMutation.isPending}
            >
              {createAspectMutation.isPending ? 'Creating...' : 'Create'}
            </button>
          </div>
        </div>
      )}
      
      <div className={styles.content}>
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <h2>Aspects</h2>
            <span className={styles.aspectCount}>{tree.aspects.length}</span>
          </div>
          {tree.aspects.length === 0 ? (
            <div className={styles.emptyAspects}>
              <p>No aspects yet</p>
              <p className={styles.hint}>Create an aspect to organize your project</p>
            </div>
          ) : (
            <AspectTree
              aspects={tree.aspects}
              selectedId={selectedAspect?.aspectId || null}
              onSelect={setSelectedAspect}
            />
          )}
        </aside>
        
        <main className={styles.main}>
          {selectedAspect ? (
            <>
              <Breadcrumbs
                items={getBreadcrumbs(selectedAspect)}
                onNavigate={(id) => {
                  const findAspect = (nodes: AspectTreeNode[], targetId: string): AspectTreeNode | null => {
                    for (const node of nodes) {
                      if (node.aspectId === targetId) return node;
                      const found = findAspect(node.children, targetId);
                      if (found) return found;
                    }
                    return null;
                  };
                  const aspect = findAspect(tree.aspects, id);
                  if (aspect) setSelectedAspect(aspect);
                }}
              />
              
              <div className={styles.aspectHeader}>
                <div>
                  <h2>{selectedAspect.name}</h2>
                  {selectedAspect.description && (
                    <p className={styles.description}>{selectedAspect.description}</p>
                  )}
                </div>
                <button
                  className="btn btn-ghost"
                  onClick={() => {
                    if (confirm('Delete this aspect?')) {
                      deleteAspectMutation.mutate(selectedAspect.aspectId);
                    }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                  Delete
                </button>
              </div>
              
              <MemoryEditor aspect={selectedAspect} />
            </>
          ) : (
            <div className={styles.noSelection}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/>
                <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/>
              </svg>
              <h3>Select an aspect</h3>
              <p>Choose an aspect from the sidebar to view and edit its memory</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

